import React from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BiSolidBot, BiSolidChat, BiSolidImageAdd, BiSolidMessageDetail, BiSolidTrash, BiSolidVideoPlus } from 'react-icons/bi';

import ProBadge from '@/components/ui/ProBadge';
import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { BiLouise } from './AppearanceSharedAi';
import { AppearanceType, IAiAppearance } from './types';

// Define your props based on what the parent passes down
interface IconCustomizationSectionProps {
  permissions: any;
  configs: AppearanceType | null | any;
  originalData: AppearanceType | null | any;
  iconImageRef: React.RefObject<HTMLInputElement>;
  iconVideoRef: React.RefObject<HTMLInputElement>;
  isIOS: boolean;
  isAndroid: boolean;
  errors: any;
  dispatch: any;
  handleResetAppearance: () => void;
  handleUpdateItem: (key: keyof IAiAppearance, value: any) => void;
}

const IconCustomizationSection: React.FC<IconCustomizationSectionProps> = ({
  permissions,
  configs,
  originalData,
  iconImageRef,
  iconVideoRef,
  isIOS,
  isAndroid,
  errors,
  dispatch,
  handleResetAppearance,
  handleUpdateItem,
}) => {
  const { t } = useTranslation();

  const iconOptions = [
    { id: 4, ui: <BiLouise /> },
    { id: 1, ui: <BiSolidBot size={25} /> },
    { id: 2, ui: <BiSolidChat size={25} /> },
    { id: 3, ui: <BiSolidMessageDetail size={25} /> },
  ];

  return (
    <div className="tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex">
      <div className="tw-self-stretch tw-text-gray-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7">
        {t('DashboardSettingCustomizeChat/floating-btn')}
      </div>

      <div className="tw-self-stretch tw-justify-start tw-items-center tw-gap-6 tw-flex tw-w-full">
        <Form.Group className="tw-mb-4">
          <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
            {t('DashboardSettingCustomizeChat/t-03')}
            {!permissions.settingsPage.widget.customization.icon && <ProBadge className="tw-ms-2" onClose={handleResetAppearance} />}
          </Form.Label>

          <div className="tw-flex tw-items-center tw-gap-5 tw-flex-wrap">
            {iconOptions.map(item => (
              <button
                id="btn-update-icon"
                key={item.id}
                type="button"
                disabled={!permissions.settingsPage.widget.customization.icon}
                className={`tw-border-0 tw-w-10 tw-h-10 ${
                  configs?.icon?.type === 'icon' && item.id === Number(configs?.icon?.value) ? 'tw-bg-blue-500' : 'tw-bg-gray-400'
                } tw-text-white tw-rounded-[999px] tw-flex tw-items-center tw-flex-col tw-justify-center`}
                onClick={() =>
                  handleUpdateItem('icon', {
                    type: 'icon',
                    value: item.id.toString(),
                  })
                }
              >
                {item.ui}
              </button>
            ))}

            {/* Image Button */}
            <button
              id="btn-update-image"
              type="button"
              disabled={!permissions.settingsPage.widget.customization.icon}
              className={`tw-pl-3 tw-pr-4 tw-py-2  ${
                configs.icon.type === 'image' ? 'tw-bg-blue-500 tw-text-white' : 'tw-bg-white tw-text-gray-400'
              } tw-rounded-[99px] tw-border tw-border-solid tw-border-gray-300 tw-justify-start tw-items-center tw-gap-2 tw-flex tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal`}
              onClick={() =>
                handleUpdateItem('icon', {
                  type: 'image',
                  value: originalData?.icon?.type === 'image' && originalData?.icon?.value ? originalData?.icon?.value : '',
                })
              }
            >
              <BiSolidImageAdd size={22} className=" tw-mr-2" />
              Image
            </button>

            {/* Video Button */}
            <button
              id="btn-select-video"
              type="button"
              disabled={!permissions.settingsPage.widget.customization.icon}
              className={`tw-pl-3 tw-pr-4 tw-py-2 ${
                configs.icon.type === 'video' ? 'tw-bg-blue-500 tw-text-white' : 'tw-bg-white tw-text-gray-400'
              } tw-rounded-[99px] tw-border tw-border-solid tw-border-gray-300 tw-justify-start tw-items-center tw-gap-2 tw-flex tw-text-gray-700 tw-text-base tw-font-normal tw-font-['Lexend'] tw-leading-normal`}
              onClick={() =>
                handleUpdateItem('icon', {
                  type: 'video',
                  value: originalData?.icon?.type === 'video' && originalData?.icon?.value ? originalData?.icon?.value : '',
                })
              }
            >
              <BiSolidVideoPlus size={22} className="tw-mr-2" />
              Video
            </button>
          </div>
        </Form.Group>
      </div>

      {/* Upload Image Section */}
      {configs.icon.type === 'image' && (
        <Form.Group className="lg:tw-w-[70%] tw-w-full tw-mb-4">
          <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
            {t('DashboardSettingCustomizeChat/upload-image')}
          </Form.Label>
          <InputGroup>
            <Form.Control
              type="file"
              accept="image/png, image/jpeg, image/jpg, image/gif"
              ref={iconImageRef}
              isInvalid={!!iconImageRef.current?.files && !configs.icon.value}
              onChange={(e: any) =>
                handleUpdateItem('icon', {
                  type: 'image',
                  value: e.target.files[0],
                })
              }
            />
            <InputGroup.Text>
              <BiSolidTrash
                size={22}
                className="tw-text-gray-400"
                onClick={() => {
                  if (iconImageRef.current) {
                    iconImageRef.current.value = '';
                    handleUpdateItem('icon', { type: 'image', value: '' });
                  }
                }}
              />
            </InputGroup.Text>
          </InputGroup>
          {configs.icon.value && typeof configs.icon.value !== 'object' && (
            <div className="tw-mt-6">
              <span className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-mb-3">Current image:</span>{' '}
              <a href={configs.icon.value} target="_blank" rel="noreferrer" className="tw-text-sm tw-mt-6 tw-text-blue-400 tw-underline">
                Link
              </a>
            </div>
          )}
        </Form.Group>
      )}

      {/* Upload Video Section */}
      {configs.icon.type === 'video' && (
        <Form.Group className="lg:tw-w-[70%] tw-w-full tw-mb-4">
          <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
            {t('DashboardSettingCustomizeChat/upload-video')}
          </Form.Label>
          <InputGroup>
            <Form.Control
              type="file"
              accept={
                isIOS
                  ? 'video/mp4, video/mov, video/m4v, video/x-matroska, video/avi'
                  : isAndroid
                  ? 'video/mp4, video/3gp, video/webm, video/x-matroska, video/avi'
                  : 'video/mp4, video/webm, video/ogg'
              }
              ref={iconVideoRef}
              isInvalid={!!iconVideoRef.current?.files && !configs.icon.value}
              onChange={(e: any) =>
                handleUpdateItem('icon', {
                  type: 'video',
                  value: e.target.files[0],
                })
              }
            />
            <InputGroup.Text>
              <BiSolidTrash
                size={22}
                className="tw-text-gray-400"
                onClick={() => {
                  if (iconVideoRef.current) {
                    iconVideoRef.current.value = '';
                    handleUpdateItem('icon', { type: 'video', value: '' });
                  }
                }}
              />
            </InputGroup.Text>
          </InputGroup>
          {configs.icon.value && typeof configs.icon.value !== 'object' && (
            <div className="tw-mt-6">
              <span className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-mb-3">Current video:</span>{' '}
              <a href={configs.icon.value} target="_blank" rel="noreferrer" className="tw-text-sm tw-text-blue-400 tw-underline">
                Link
              </a>
            </div>
          )}
        </Form.Group>
      )}

      {/* Video Shape */}
      {configs.icon.type === 'video' && (
        <Form.Group className="tw-mb-4">
          <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
            {t('DashboardSettingCustomizeChat/button-shape')}
            {!permissions.settingsPage.widget.customization.roundedVideo && <ProBadge className="tw-ms-2" />}
          </Form.Label>
          <div className="tw-flex tw-items-center tw-gap-6">
            <button
              id="btn-update-rounded-video"
              type="button"
              onClick={() => handleUpdateItem('roundedVideo', false)}
              className={`${
                !configs.roundedVideo ? 'tw-border-4 tw-border-solid tw-border-blue-500' : 'tw-border-4 tw-border-solid tw-border-transparent'
              } tw-w-9 tw-h-10 tw-bg-gray-300 tw-rounded ${configs.icon.type !== 'video' ? 'tw-cursor-not-allowed' : ''}`}
            />
            <button
              id="btn-update-rounded-video-2"
              type="button"
              onClick={() => handleUpdateItem('roundedVideo', true)}
              className={`${
                configs.roundedVideo ? 'tw-border-4 tw-border-solid tw-border-blue-500' : 'tw-border-4 tw-border-solid tw-border-transparent'
              } tw-w-10 tw-h-10 tw-bg-gray-300 tw-rounded-full`}
            />
          </div>
        </Form.Group>
      )}

      {errors.icon && <span className="tw-text-red-500 tw-text-sm tw-block">{errors.icon?.message}</span>}
    </div>
  );
};

export default WrappedErrorBoundary(IconCustomizationSection);
